import { Modal } from '../../../components/modal/modal';
import { useState } from 'react';
import { Spinner } from '../../../components/spinner/spinner';
import { Location } from '../../../clients/quipli-service/location/location';
import FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { Typography } from '../../../components/typography/typography';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { ReportingPeriod } from '../../../clients/quipli-service/stats/stats';
import { toast } from '../../../util/toasts';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { BasisCategory } from '../../../clients/quipli-service/report/report';

export const RevenueExportModal = ({
  isOpen,
  setIsOpen,
  from,
  to,
  period,
  basis,
  location,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  from: DateTime;
  to?: DateTime;
  period: ReportingPeriod;
  basis: 'accrual' | 'cash';
  location?: Location | null;
}) => {
  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [includeNonTax, setIncludeNonTax] = useState<boolean>(true);
  const [includeTaxable, setIncludeTaxable] = useState<boolean>(true);
  const [includeTax, setIncludeTax] = useState<boolean>(true);
  const { exportRevenueReport } = useReportApiV2();

  // Callbacks
  const handleExport = async () => {
    try {
      setRequestInProgress(true);
      const file = await exportRevenueReport({
        start: from,
        end: to ?? undefined,
        period,
        basis,
        locationId: location?.id ?? undefined,
        categories: [
          ...(includeTax ? ['Tax'] : []),
          ...(includeNonTax ? ['NonTaxable'] : []),
          ...(includeTaxable ? ['Taxable'] : []),
        ] as BasisCategory[],
      });
      const exportFileName = `Revenue_Report_${from.toFormat('D').replaceAll('/', '-')}_${(to ?? DateTime.local())
        .toFormat('D')
        .replaceAll('/', '-')}_${basis}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';
      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      toast('Error exporting revenue report', 'error');
    } finally {
      setRequestInProgress(false);
      closeModal();
    }
  };

  const isButtonDisabled = (): boolean => {
    return isRequestInProgress || (!includeTaxable && !includeTax && !includeNonTax);
  };

  const closeModal = () => {
    // Don't close modal if there is a request in progress
    if (isRequestInProgress) return;

    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      title={'Export Reports'}
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          <>
            <button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={() => closeModal()}
            >
              Cancel
            </button>

            <button
              className="button button-small button-block button-primary"
              disabled={isButtonDisabled()}
              onClick={() => handleExport()}
            >
              {'Export'}
            </button>
          </>
        )
      }
      close={closeModal}
    >
      <div>
        <div className="text-center px-4 mb-4">
          {isRequestInProgress ? (
            <div className={'flex flex-col gap-2'}>
              <Typography type="text-sm" className="text-grey-500">
                <div>We're generating your files, this may take a moment.</div>
              </Typography>
            </div>
          ) : (
            <div className={'flex flex-col gap-2'}>
              <Typography type="text-sm" className="text-grey-500">
                <div>Select the reports you want to export.</div>
              </Typography>
              <div className={'flex flex-col gap-2'}>
                <Checkbox
                  label={'Taxable Revenue'}
                  onChange={(isChecked) => setIncludeTaxable(isChecked)}
                  isChecked={includeTaxable}
                />
                <Checkbox
                  label={'Non-taxable Revenue'}
                  onChange={(isChecked) => setIncludeNonTax(isChecked)}
                  isChecked={includeNonTax}
                />
                <Checkbox
                  label={'Tax Revenue'}
                  onChange={(isChecked) => setIncludeTax(isChecked)}
                  isChecked={includeTax}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
